import React from "react"
import { Container, Row, Col, Form, Input, FormGroup, Alert } from "reactstrap"
import stellarProcessing from "../../../view/assets/stellar-animation.gif"
import { Trans, useLingui } from "@lingui/react"
import PropTypes from "prop-types"
import { toast } from "react-toastify"
import { WarningMessage } from "../../../view/shared/notifications/Notifications"
import validateInput from "../../../view/shared/validations/validateAmount"
import { connect } from "react-redux"
import LastLoanRepayment from "../../reverseTransactions/LastLoanRepayment"
import { currentTransactionContext } from "../../../store/actions/transactionContext"
import { selectMemberTotalLoanAggregate } from "../../../store/reducers/getMemberAggregates"
import { selectMemberServiceChargeAggregate } from "../../../store/reducers/getMemberAggregates"
import { getCurrency } from "../../../view/shared/utilities/currency"
import { initiateTransaction } from "../../../store/reducers/transactions/actions"
import { selectCreatedVslaInfo } from "../../../store/reducers/getVslas"
import { setCurrentPage } from "../../../store/reducers/navigation"
import styles from "./repay.module.css"

const RepayLoan = (props) => {
  const { i18n } = useLingui()
  const notify = (msg) => toast(msg)
  const [state, setState] = React.useState({
    amount: 0,
    service_charge_amount: "",
    loading: false,
    errors: {},
    transactionTypeInProcessing: [],
  })

  React.useEffect(() => {
    props.pushTransactionContext("Loan repaid")
  }, [])

  const onChange = (e) => setState({ ...state, [e.target.name]: e.target.value })

  const isValid = () => {
    const { errors, isValid } = validateInput(state)

    if (!isValid) {
      setState({ ...state, errors })
    }

    return isValid
  }
  const redirectBack = () => {
    props.setPage("group-memberActions")
  }

  const onSubmit = (e) => {
    if (
      state.transactionTypeInProcessing.includes("LOAN_INTEREST_REPAYMENT") ||
      state.transactionTypeInProcessing.includes("LOAN_REPAYMENT")
    ) {
      return
    }
    e.preventDefault()
    if (isValid()) {
      processTransaction(state.amount, state.service_charge_amount)
      setState({ ...state, amount: "" })
    }
  }

  const isPayable = () => {
    let outstandingLoan = 0
    const memberId = props.currentMember?.pubkey;

    if (props.memberDebt(memberId)?.balance > 0) {
      outstandingLoan = props.memberDebt(memberId).balance
      return outstandingLoan
    }

    return parseFloat(outstandingLoan)
  }

  const processTransaction = (amount, service_charge) => {
    const lang_code = i18n.locale
    const memberId = props.currentMember?.pubkey;
    const interest =
      props.loanInterest(memberId)?.balance > 0
    const amount_msg = parseFloat(interest ? service_charge : amount)

    const lang = {
      fr: {
        message: `Confirmer le remboursement du prêt de ${amount_msg} pour le membre`,
      },
      en: {
        message: `Confirm loan repayment of ${amount_msg} for member`,
      },
      sw: {
        message: `Thibitisha kwamba mwanachama amerejesha pesa wa ${amount_msg}`,
      },
      ZARMA: {
        message: `Zarma for Confirm loan repayment of ${amount_msg} for member`,
      },
      HAOUSSA: {
        message: `Tabbatar da ${amount_msg} dala dari  da ỳar rukuni ta ɗauka `,
      },
      es: {
        message: `Confirmar el pago del préstamo de ${amount_msg} para el miembro`,
      },
      lb: {
        message: `Confirm loan repayment of ${amount_msg} for household`,
      },
      pt: {
        message: `Confirme o pagamento do empréstimo de ${amount_msg} para o membro`,
      },
      ar: {
        message: "أكد معاملة سداد القرض",
      },
    }

    const meetingId = props.ongoingMeeting?.id ? props.ongoingMeeting?.id : props.ongoingMeeting?.cacheId

    if (
      window.confirm(lang[lang_code].message) &&
      parseFloat(amount) <= isPayable()
    ) {
      setState({ ...state, loading: true })
      const uuid = props.currentMember.pubkey

      if (interest) {
        props.commitTransaction(
          {
            uid: uuid,
            type: "LOAN_INTEREST_REPAYMENT",
            amount: parseFloat(service_charge),
            paysInterest: true,
            ...(props.currentMember?.id ? {
              member_id: props.currentMember?.id,
            } : {
              memberCacheId: props.currentMember?.cacheId || props.currentMember?.pubkey
            })
          },
        )
      } else {
        props.commitTransaction(
          {
            uid: uuid,
            type: "LOAN_REPAYMENT",
            amount: parseFloat(amount),
            ...(props.currentMember?.id ? {
              member_id: props.currentMember?.id,
            } : {
              memberCacheId: props.currentMember?.cacheId || props.currentMember?.pubkey
            })
          },
        )
      }
      notify(<WarningMessage msg={"Processing Transaction"} />)
      props.setPage("group-memberList")
    } else {
      notify(<WarningMessage />)
    }
  }

  const debtAmount = () => {
    const memberId = props.currentMember?.pubkey;
    const debt =
      props.memberDebt(memberId)?.balance > 0
        ? props.memberDebt(memberId).balance
        : "0.00"
    return debt
  }

  const disableLoanInterestReversal = () => {
    const { transactions, currentMember, loanInterest } = props
    const memberId = currentMember?.pubkey;
    const hasLoanRepayments = transactions.some(
      ({ type }) => type === "LOAN_REPAYMENT",
    )
    const hasZeroLoanInterestBalance =
      loanInterest(memberId)?.balance === 0
    return hasZeroLoanInterestBalance && hasLoanRepayments
  }

  const service_charge_amount = "Service Charge Amount"
  const payment_amount = "Payment Amount"
  const repay_submit = "Repay"

  if (!state.loading) {
    return (
      <Container>
        <div>
          <h4 className={styles.header}>{props.currentMember?.full_name}</h4>
        </div>
        <div className={styles.header}>
          <Trans id="loan.repayment">Loan Repayment</Trans>
        </div>

        <div className={styles.info}>
          <p>
            <Trans id="outstanding.loan">Outstanding Loan</Trans>{" "}
            {props.currentMember?.loan_service_charge
              ? parseFloat(debtAmount()).toFixed(2)
              : parseFloat(isPayable()).toFixed(2)}{" "}
            {getCurrency(props.group?.country).symbol}
          </p>
          {props.currentMember?.loan_service_charge &&
            props.loanInterest(props.currentMember.pubkey)
              ?.balance > 0 && (
              <p>
                <Trans id="outstanding.service.charge">
                  Outstanding Service Charge
                </Trans>{" "}
                {Math.abs(
                  parseFloat(
                    props.loanInterest(props.currentMember.pubkey)
                      ?.balance,
                  ).toFixed(2),
                )}{" "}
                {getCurrency(props.group?.country).symbol}
              </p>
            )}
        </div>
        {props.loanInterest(props.currentMember?.pubkey)?.balance >
          0 && (
            <Alert color="warning">
              <span>
                <Trans id="pay.service.charge.first">
                  Please pay outstanding service charge first
                </Trans>
              </span>
            </Alert>
          )}
        <Row>
          <br />
          <Col>
            {props.memberDebt(props.currentMember?.pubkey)?.balance >
              0 && (
                <Form onSubmit={onSubmit} noValidate>
                  <Container>
                    {!props.loanInterest(props.currentMember?.pubkey)
                      ?.balance && (
                        <FormGroup>
                          <span className={styles.errorMessage}>{state.errors.amount}</span>
                          <Input
                            type="number"
                            name="amount"
                            className={styles.formInput}
                            placeholder={payment_amount}
                            onChange={onChange}
                          />
                        </FormGroup>
                      )}
                    {props.currentMember?.loan_service_charge &&
                      props.loanInterest(props.currentMember?.pubkey)
                        ?.balance > 0 && (
                        <FormGroup>
                          <span className={styles.errorMessage}>
                            {state.errors.service_charge_amount}
                          </span>
                          <Input
                            type="number"
                            name="service_charge_amount"
                            className={styles.formInput}
                            placeholder={service_charge_amount}
                            onChange={onChange}
                          />
                        </FormGroup>
                      )}
                  </Container>

                  <Container>
                    <FormGroup>
                      <Input
                        type="submit"
                        value={repay_submit}
                        name="submit"
                        className={`btn btn-primary ${styles.submitButton}`}
                        disabled={
                          state.transactionTypeInProcessing.includes(
                            "LOAN_INTEREST_REPAYMENT",
                          ) ||
                            state.transactionTypeInProcessing.includes(
                              "LOAN_REPAYMENT",
                            ) ||
                            parseFloat(state.service_charge_amount) >
                            Math.abs(
                              parseFloat(
                                props.loanInterest(
                                  props.currentMember.pubkey,
                                )?.balance,
                              ),
                            )
                            ? true
                            : false
                        }
                      />
                    </FormGroup>
                  </Container>
                </Form>
              )}
          </Col>
        </Row>
        <LastLoanRepayment
          redirectBack={redirectBack}
          reversal_details={{
            destFundType: "issuer",
            reversalTransactionType: "Loan repayment reversed",
          }}
          transactions={props.transactions}
          disableLoanInterestReversal={disableLoanInterestReversal()}
        />
      </Container>
    )
  }

  return (
    <div className={styles.spinnerContainer}>
      <img src={stellarProcessing} className={styles.spinner} alt="Stellar" />
    </div>
  )
}

RepayLoan.propTypes = {
  vsla: PropTypes.object,
  pushTransactionContext: PropTypes.func,
  setPage: PropTypes.func,
  memberDebt: PropTypes.func,
  currentMember: PropTypes.object,
  loanInterest: PropTypes.func,
  commitTransaction: PropTypes.func,
  ongoingMeeting: PropTypes.array,
  group: PropTypes.object,
  transactions: PropTypes.array,
}

const mapStateToProps = (state) => {
  const { account, accountV2, loadedMember } = state;

  const createMemberBalanceSelector = (accountType) => (pubkey) => {
    if (!pubkey) return { balance: 0 }; // Return a default object if no pubkey

    // Priority 1: Check offline state using pubkey
    if (accountV2.offline?.members[pubkey]) {
      return accountV2.offline.members[pubkey][accountType];
    }

    // Priority 3: Check online state using wallet ID
    if (account.members[pubkey]) {
      return account.members[pubkey][accountType];
    }

    return { balance: 0 }; // Return a default object if not found
  };

  const selectMemberReversableTransactions = (types) => {
    const memberPubkey = loadedMember?.pubkey
    if (!memberPubkey) {
      return []
    }
    else if (!accountV2.online?.transactions) {
      return []
    }
    return accountV2.online?.transactions?.[memberPubkey].filter(tx => types.includes(tx.type)) || []
  }

  return {
    vsla: state.vsla,
    pendingTransactionsKeys: Object.keys(state.pendingTransactions).filter((key) => {
      const fractions = key.split("_")
      return fractions.length >= 3 && fractions.includes(state.vsla?.id)
    }),
    ongoingMeeting: state.currentOngoingMeeting,
    memberDebt: createMemberBalanceSelector('loan'),
    loanInterest: createMemberBalanceSelector('loan_interest'),
    memberDebtAndInterest: selectMemberTotalLoanAggregate(state),
    membersInterest: selectMemberServiceChargeAggregate(state),
    currentMember: loadedMember,
    transactions: selectMemberReversableTransactions(["LOAN_REPAYMENT", "LOAN_INTEREST_REPAYMENT"]),
    group: state.group,
    vslaSummary: selectCreatedVslaInfo(state),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setPage: (page) => dispatch(setCurrentPage(page)),
    commitTransaction: (data) =>
      dispatch(initiateTransaction(data)),
    pushTransactionContext: (type) => dispatch(currentTransactionContext(type)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RepayLoan)
